import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Tent,
  FileSpreadsheet,
  FileText,
  Eye,
  ChevronLeft,
  Upload,
  Loader2,
  Image as ImageIcon,
  Mail,
  Send,
  Lock,
  Unlock,
  EyeOff,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Reusable Image Uploader Component
const ImageUploader = ({ images, onChange, single = false, label = "Naloži" }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    const newImages = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data.url) {
          newImages.push(`${BACKEND_URL}${data.url}`);
        }
      } catch (err) {
        toast.error(`Napaka pri nalaganju: ${file.name}`);
      }
    }

    if (single) {
      onChange(newImages[0] || '');
    } else {
      onChange([...(images || []), ...newImages]);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    if (single) {
      onChange('');
    } else {
      const updated = [...(images || [])];
      updated.splice(index, 1);
      onChange(updated);
    }
  };

  const imageList = single ? (images ? [images] : []) : (images || []);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        {imageList.map((img, idx) => (
          <div key={idx} className="relative group">
            <img
              src={img}
              alt={`Slika ${idx + 1}`}
              className="w-24 h-24 object-cover rounded-lg border border-stone-700"
            />
            <button
              type="button"
              onClick={() => removeImage(idx)}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 hover:bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || (single && imageList.length > 0)}
          className="w-24 h-24 border-2 border-dashed border-stone-600 hover:border-emerald-500 rounded-lg flex flex-col items-center justify-center text-stone-400 hover:text-emerald-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <>
              <Upload className="w-6 h-6 mb-1" />
              <span className="text-xs">{label}</span>
            </>
          )}
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={!single}
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

const EMPTY_CAMP = {
  year: new Date().getFullYear(),
  title_sl: '',
  title_en: '',
  description_sl: '',
  description_en: '',
  start_date: '',
  end_date: '',
  location: '',
  max_participants: 30,
  registration_open: true,
  is_active: true,
  image: '',
  images: [],
};

const statusBadge = (status) => {
  const map = {
    pending: { label: 'Čaka', cls: 'bg-amber-700/30 text-amber-300 border-amber-600/40' },
    approved: { label: 'Potrjena', cls: 'bg-emerald-700/30 text-emerald-300 border-emerald-600/40' },
    rejected: { label: 'Zavrnjena', cls: 'bg-red-700/30 text-red-300 border-red-600/40' },
    waitlist: { label: 'Čakalna lista', cls: 'bg-orange-700/30 text-orange-300 border-orange-600/40' },
  };
  const s = map[status] || map.pending;
  return <Badge className={`${s.cls} border`}>{s.label}</Badge>;
};

const AdminCamps = () => {
  const [view, setView] = useState('list'); // 'list' | 'registrations'
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_CAMP);

  const [selectedCamp, setSelectedCamp] = useState(null);
  const [regs, setRegs] = useState([]);
  const [regsLoading, setRegsLoading] = useState(false);
  const [regDetail, setRegDetail] = useState(null);

  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailTab, setEmailTab] = useState('edit'); // 'edit' | 'preview'
  const [singleRecipient, setSingleRecipient] = useState(null); // For sending to single parent
  const [emailData, setEmailData] = useState({
    subject: '',
    content: '',
    sendTo: 'all',
  });

  const EMAIL_TEMPLATES = {
    info: {
      subject: 'Informacije o lovskem taboru',
      content: `<h3>Spoštovani starši,</h3>

<p>Pred nami je <strong>lovski tabor</strong>, na katerega ste prijavili vašega otroka. V nadaljevanju vam posredujemo vse potrebne informacije.</p>

<h4>📅 Prihod in odhod</h4>
<p><strong>Prihod:</strong> [dan, datum] ob [uri] na [lokacija]<br/>
<strong>Odhod:</strong> [dan, datum] ob [uri]</p>

<h4>🎒 Kaj otrok potrebuje s seboj</h4>
<ul>
  <li>Oblačila primerna za na prostem (dolge hlače, majice z dolgimi rokavi)</li>
  <li>Dežna jakna in nepremočljivi čevlji</li>
  <li>Copati za v koči</li>
  <li>Toaletne potrebščine (zobna ščetka, pasta, brisača, itd.)</li>
  <li>Spalna vreča ali posteljnina</li>
  <li>Pijača za prvi dan (plastenka vode)</li>
  <li>Po želji: manjši žepnina</li>
</ul>

<h4>📞 Kontakt v nujnih primerih</h4>
<p>Med taborom nas lahko dosežete na telefonski številki: <strong>[telefonska številka]</strong></p>

<p>Veselimo se skupnega časa z vašimi otroki!</p>

<p>Lovski pozdrav,<br/>
<strong>Lovska družina Rakek</strong></p>`,
    },
    reminder: {
      subject: 'Opomnik: Lovski tabor se približuje',
      content: `<h3>Spoštovani starši,</h3>

<p>Prijazno vas obveščamo, da se <strong>lovski tabor</strong> hitro približuje.</p>

<p>Prosimo vas, da preverite, ali ima vaš otrok vse potrebno pripravljeno (glej prejšnji email z informacijami).</p>

<p>V primeru kakršnihkoli sprememb (bolezen, odsotnost) nas prosimo pravočasno obvestite.</p>

<p>Se vidimo kmalu!</p>

<p>Lovski pozdrav,<br/>
<strong>Lovska družina Rakek</strong></p>`,
    },
  };

  const fetchCamps = async () => {
    try {
      const { data } = await axios.get(`${API}/camps`);
      setCamps(data || []);
    } catch (err) {
      toast.error('Napaka pri nalaganju taborov');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/camps`);
        if (!cancelled) setCamps(data || []);
      } catch (err) {
        if (!cancelled) toast.error('Napaka pri nalaganju taborov');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, year: parseInt(formData.year, 10), max_participants: parseInt(formData.max_participants, 10) || 0 };
      if (editingId) {
        await axios.put(`${API}/camps/${editingId}`, payload);
        toast.success('Tabor je bil posodobljen');
      } else {
        await axios.post(`${API}/camps`, payload);
        toast.success('Nov tabor je bil dodan');
      }
      resetForm();
      fetchCamps();
    } catch (err) {
      toast.error('Napaka pri shranjevanju');
    }
  };

  const handleEdit = (camp) => {
    setEditingId(camp.id);
    setFormData({
      year: camp.year,
      title_sl: camp.title_sl || '',
      title_en: camp.title_en || '',
      description_sl: camp.description_sl || '',
      description_en: camp.description_en || '',
      start_date: camp.start_date || '',
      end_date: camp.end_date || '',
      location: camp.location || '',
      max_participants: camp.max_participants || 30,
      registration_open: !!camp.registration_open,
      is_active: !!camp.is_active,
      image: camp.image || '',
      images: camp.images || [],
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Izbrišem tabor in vse pripadajoče prijave?')) return;
    try {
      await axios.delete(`${API}/camps/${id}`);
      toast.success('Tabor izbrisan');
      fetchCamps();
    } catch (err) {
      toast.error('Napaka pri brisanju');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setShowForm(false);
    setFormData(EMPTY_CAMP);
  };

  const toggleRegistrations = async (camp) => {
    const newState = !camp.registration_open;
    try {
      await axios.put(`${API}/camps/${camp.id}`, {
        registration_open: newState,
      });
      toast.success(newState ? 'Prijave odprte' : 'Prijave zaprte');
      fetchCamps();
    } catch (err) {
      toast.error('Napaka pri posodabljanju');
    }
  };

  const toggleActive = async (camp) => {
    const newState = !camp.is_active;
    try {
      await axios.put(`${API}/camps/${camp.id}`, {
        is_active: newState,
      });
      toast.success(newState ? 'Tabor aktiviran' : 'Tabor skrit');
      fetchCamps();
    } catch (err) {
      toast.error('Napaka pri posodabljanju');
    }
  };

  const handleSendInfoEmail = async () => {
    if (!emailData.subject.trim() || !emailData.content.trim()) {
      toast.error('Vnesite zadevo in vsebino');
      return;
    }
    if (!selectedCamp) return;

    setEmailSending(true);
    try {
      // If sending to single recipient
      if (singleRecipient) {
        const { data } = await axios.post(`${API}/camps/${selectedCamp.id}/send-single-email`, {
          subject: emailData.subject,
          content: emailData.content,
          recipient_email: singleRecipient.parent_email,
          recipient_name: singleRecipient.parent_name,
        });
        
        if (data.success) {
          toast.success(`Email poslan: ${singleRecipient.parent_name}`);
          setShowEmailModal(false);
          setSingleRecipient(null);
          setEmailData({ subject: '', content: '', sendTo: 'all' });
        }
      } else {
        // Sending to multiple recipients
        const { data } = await axios.post(`${API}/camps/${selectedCamp.id}/send-info-email`, {
          subject: emailData.subject,
          content: emailData.content,
          send_to: emailData.sendTo,
        });
        
        if (data.success) {
          toast.success(`Email uspešno poslan ${data.sent_count} prejemnikom`);
          if (data.failed_emails?.length > 0) {
            toast.warning(`Napaka pri: ${data.failed_emails.join(', ')}`);
          }
          setShowEmailModal(false);
          setEmailData({ subject: '', content: '', sendTo: 'all' });
        }
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Napaka pri pošiljanju';
      toast.error(msg);
    } finally {
      setEmailSending(false);
    }
  };

  const applyEmailTemplate = (templateKey) => {
    const template = EMAIL_TEMPLATES[templateKey];
    if (template) {
      setEmailData(prev => ({
        ...prev,
        subject: template.subject,
        content: template.content,
      }));
    }
  };

  const openEmailForSingle = (registration) => {
    setSingleRecipient(registration);
    setEmailData({ subject: '', content: '', sendTo: 'single' });
    setEmailTab('edit');
    setShowEmailModal(true);
  };

  const openEmailForAll = () => {
    setSingleRecipient(null);
    setEmailData({ subject: '', content: '', sendTo: 'all' });
    setEmailTab('edit');
    setShowEmailModal(true);
  };

  const openRegistrations = async (camp) => {
    setSelectedCamp(camp);
    setView('registrations');
    setRegsLoading(true);
    try {
      const { data } = await axios.get(`${API}/camps/${camp.id}/registrations`);
      setRegs(data || []);
    } catch (err) {
      toast.error('Napaka pri nalaganju prijav');
    } finally {
      setRegsLoading(false);
    }
  };

  const refreshRegs = async () => {
    if (!selectedCamp) return;
    const { data } = await axios.get(`${API}/camps/${selectedCamp.id}/registrations`);
    setRegs(data || []);
  };

  const downloadExport = async (kind) => {
    if (!selectedCamp) return;
    try {
      const res = await axios.get(`${API}/camps/${selectedCamp.id}/export/${kind}`, {
        responseType: 'blob',
      });
      const ext = kind === 'excel' ? 'xlsx' : 'pdf';
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `prijave-tabor-${selectedCamp.year}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${ext.toUpperCase()} izvožen`);
    } catch (err) {
      toast.error('Napaka pri izvozu');
    }
  };

  const updateStatus = async (reg, status) => {
    try {
      await axios.put(`${API}/camps/registrations/${reg.id}`, { status });
      toast.success('Status posodobljen');
      refreshRegs();
    } catch (err) {
      toast.error('Napaka');
    }
  };

  const deleteReg = async (reg) => {
    if (!window.confirm(`Izbrišem prijavo za ${reg.child_first_name} ${reg.child_last_name}?`)) return;
    try {
      await axios.delete(`${API}/camps/registrations/${reg.id}`);
      toast.success('Prijava izbrisana');
      refreshRegs();
    } catch (err) {
      toast.error('Napaka');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ============ REGISTRATIONS VIEW ============
  if (view === 'registrations' && selectedCamp) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setView('list'); setSelectedCamp(null); setRegs([]); }}
              className="border-stone-700 text-stone-300 hover:bg-stone-800"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Nazaj
            </Button>
            <Tent className="w-6 h-6 text-emerald-400" />
            <div>
              <h2 className="text-xl font-semibold text-white">
                Prijave za tabor {selectedCamp.year}
              </h2>
              <p className="text-stone-400 text-sm">
                {selectedCamp.title_sl} · {regs.filter(r => r.status !== 'waitlist').length} prijav
                {regs.filter(r => r.status === 'waitlist').length > 0 && (
                  <span className="text-orange-400 ml-1">
                    + {regs.filter(r => r.status === 'waitlist').length} na čakalni listi
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              data-testid="send-info-email-btn"
              onClick={openEmailForAll}
              className="bg-blue-600 hover:bg-blue-500"
            >
              <Mail className="w-4 h-4 mr-2" /> Pošlji info email
            </Button>
            <Button
              data-testid="export-excel-btn"
              onClick={() => downloadExport('excel')}
              className="bg-emerald-600 hover:bg-emerald-500"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
            </Button>
            <Button
              data-testid="export-pdf-btn"
              onClick={() => downloadExport('pdf')}
              variant="outline"
              className="border-stone-700 text-stone-200 hover:bg-stone-800"
            >
              <FileText className="w-4 h-4 mr-2" /> PDF
            </Button>
          </div>
        </div>

        {regsLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : regs.length === 0 ? (
          <Card className="bg-stone-800/50 border-stone-700/50 p-8 text-center">
            <p className="text-stone-400">Še ni prijav za ta tabor.</p>
          </Card>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="registrations-table">
              <thead>
                <tr className="text-stone-400 border-b border-stone-700">
                  <th className="text-left py-2 px-2">Datum</th>
                  <th className="text-left py-2 px-2">Otrok</th>
                  <th className="text-left py-2 px-2">Starost / Spol</th>
                  <th className="text-left py-2 px-2">Starš</th>
                  <th className="text-left py-2 px-2">Kontakt</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-right py-2 px-2">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {regs.map((r) => (
                  <tr key={r.id} className="border-b border-stone-800 hover:bg-stone-800/40">
                    <td className="py-2 px-2 text-stone-400">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString('sl-SI') : '-'}
                    </td>
                    <td className="py-2 px-2 text-white font-medium">
                      {r.child_first_name} {r.child_last_name}
                    </td>
                    <td className="py-2 px-2 text-stone-300">
                      {r.child_birth_date} · {r.child_gender === 'M' ? 'M' : 'Ž'}
                    </td>
                    <td className="py-2 px-2 text-stone-300">{r.parent_name}</td>
                    <td className="py-2 px-2 text-stone-400">
                      <div>{r.parent_phone}</div>
                      <div className="text-xs">{r.parent_email}</div>
                    </td>
                    <td className="py-2 px-2">{statusBadge(r.status)}</td>
                    <td className="py-2 px-2">
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="outline" size="sm"
                          onClick={() => openEmailForSingle(r)}
                          className="border-blue-800/50 text-blue-400 hover:bg-blue-900/20"
                          title="Pošlji email"
                          data-testid={`email-reg-${r.id}`}
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline" size="sm"
                          onClick={() => setRegDetail(r)}
                          className="border-stone-700 text-stone-300 hover:bg-stone-800"
                          data-testid={`view-reg-${r.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Select
                          value={r.status}
                          onValueChange={(v) => updateStatus(r, v)}
                        >
                          <SelectTrigger className="w-28 h-8 bg-stone-900/60 border-stone-700 text-stone-200 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-stone-900 border-stone-700 text-white">
                            <SelectItem value="pending">Čaka</SelectItem>
                            <SelectItem value="approved">Potrjena</SelectItem>
                            <SelectItem value="waitlist">Čakalna lista</SelectItem>
                            <SelectItem value="rejected">Zavrnjena</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline" size="sm"
                          onClick={() => deleteReg(r)}
                          className="border-red-900/50 text-red-400 hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Detail dialog */}
        <Dialog open={!!regDetail} onOpenChange={(o) => !o && setRegDetail(null)}>
          <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {regDetail?.child_first_name} {regDetail?.child_last_name}
              </DialogTitle>
              <DialogDescription className="text-stone-400">
                Podrobnosti prijave
              </DialogDescription>
            </DialogHeader>
            {regDetail && (
              <div className="space-y-3 text-sm">
                <DetailRow label="Rojstni datum">{regDetail.child_birth_date}</DetailRow>
                <DetailRow label="Spol">{regDetail.child_gender === 'M' ? 'Moški' : 'Ženski'}</DetailRow>
                <DetailRow label="Telefon otroka">{regDetail.child_phone || '—'}</DetailRow>
                <DetailRow label="Email otroka">{regDetail.child_email || '—'}</DetailRow>
                <DetailRow label="Alergije">{regDetail.allergies || '—'}</DetailRow>
                <DetailRow label="Prehranske omejitve">{regDetail.dietary_restrictions || '—'}</DetailRow>
                <DetailRow label="Zdravstvene omejitve">{regDetail.health_notes || '—'}</DetailRow>
                <DetailRow label="Starš/skrbnik">{regDetail.parent_name}</DetailRow>
                <DetailRow label="Telefon starša">{regDetail.parent_phone}</DetailRow>
                <DetailRow label="Email starša">{regDetail.parent_email}</DetailRow>
                <DetailRow label="Naslov">{regDetail.address || '—'}</DetailRow>
                <div className="pt-2 border-t border-stone-700/50 space-y-1">
                  <ConsentRow ok={regDetail.consent_data_usage} label="Obdelava osebnih podatkov" />
                  <ConsentRow ok={regDetail.consent_photo_website} label="Objava slik na spletni strani LD Rakek" />
                  <ConsentRow ok={regDetail.consent_photo_lovec} label="Objava v glasilu Lovec" />
                  <ConsentRow ok={regDetail.consent_photo_slivniski} label="Objava v Slivniški pogledi" />
                </div>
                {regDetail.notes && <DetailRow label="Opombe">{regDetail.notes}</DetailRow>}
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setRegDetail(null)} className="bg-emerald-600 hover:bg-emerald-500">
                Zapri
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Email Modal */}
        <Dialog open={showEmailModal} onOpenChange={(open) => { setShowEmailModal(open); if (!open) { setEmailTab('edit'); setSingleRecipient(null); } }}>
          <DialogContent className="max-w-4xl bg-stone-900 border-stone-700 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-400" />
                {singleRecipient ? 'Pošlji email staršu' : 'Pošlji informativni email'}
              </DialogTitle>
              <DialogDescription className="text-stone-400">
                {singleRecipient 
                  ? `Pošljite email staršu: ${singleRecipient.parent_name} (${singleRecipient.parent_email})`
                  : 'Pošljite email vsem prijavljenim staršem z informacijami o taboru.'
                }
              </DialogDescription>
            </DialogHeader>

            {/* Tabs */}
            <div className="flex border-b border-stone-700 mb-4">
              <button
                onClick={() => setEmailTab('edit')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  emailTab === 'edit'
                    ? 'text-emerald-400 border-b-2 border-emerald-400'
                    : 'text-stone-400 hover:text-stone-200'
                }`}
              >
                ✏️ Uredi
              </button>
              <button
                onClick={() => setEmailTab('preview')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  emailTab === 'preview'
                    ? 'text-emerald-400 border-b-2 border-emerald-400'
                    : 'text-stone-400 hover:text-stone-200'
                }`}
              >
                👁️ Predogled
              </button>
            </div>

            {emailTab === 'edit' ? (
              <div className="space-y-4">
                {/* Templates */}
                <div className="space-y-2">
                  <Label className="text-stone-300">Predloge</Label>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => applyEmailTemplate('info')}
                      className="border-stone-700 text-stone-300 hover:bg-stone-800"
                    >
                      📋 Informacije o taboru
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => applyEmailTemplate('reminder')}
                      className="border-stone-700 text-stone-300 hover:bg-stone-800"
                    >
                      ⏰ Opomnik
                    </Button>
                  </div>
                </div>

                {/* Send To - only show when not single recipient */}
                {!singleRecipient && (
                  <div className="space-y-2">
                    <Label className="text-stone-300">Pošlji</Label>
                    <Select
                      value={emailData.sendTo}
                      onValueChange={(v) => setEmailData({ ...emailData, sendTo: v })}
                    >
                      <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-stone-800 border-stone-700">
                        <SelectItem value="all">Vsem prijavljenim</SelectItem>
                        <SelectItem value="confirmed">Samo potrjenim</SelectItem>
                        <SelectItem value="waitlist">Samo čakalni listi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Single recipient info */}
                {singleRecipient && (
                  <div className="bg-blue-900/20 rounded-lg p-3 border border-blue-700/50">
                    <p className="text-blue-300 text-sm">
                      <strong>Prejemnik:</strong> {singleRecipient.parent_name}
                    </p>
                    <p className="text-blue-400 text-sm">
                      <strong>Email:</strong> {singleRecipient.parent_email}
                    </p>
                    <p className="text-stone-400 text-sm mt-1">
                      <strong>Otrok:</strong> {singleRecipient.child_first_name} {singleRecipient.child_last_name}
                    </p>
                  </div>
                )}

                {/* Subject */}
                <div className="space-y-2">
                  <Label className="text-stone-300">Zadeva</Label>
                  <Input
                    value={emailData.subject}
                    onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                    className="bg-stone-800 border-stone-700 text-white"
                    placeholder="Informacije o lovskem taboru 2026"
                  />
                </div>

                {/* Content */}
                <div className="space-y-2">
                  <Label className="text-stone-300">Vsebina (HTML)</Label>
                  <Textarea
                    value={emailData.content}
                    onChange={(e) => setEmailData({ ...emailData, content: e.target.value })}
                    className="bg-stone-800 border-stone-700 text-white font-mono text-sm min-h-[300px]"
                    placeholder="<h3>Spoštovani starši,</h3><p>...</p>"
                  />
                  <p className="text-xs text-stone-500">
                    Uporabite HTML oznake: &lt;h3&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;li&gt;, itd.
                  </p>
                </div>

                {/* Recipients count - only for mass emails */}
                {!singleRecipient && selectedCamp && (
                  <div className="bg-stone-800/50 rounded-lg p-3 border border-stone-700">
                    <p className="text-stone-300 text-sm">
                      <strong>Tabor:</strong> {selectedCamp.title_sl} ({selectedCamp.year})
                    </p>
                    <p className="text-stone-400 text-sm">
                      <strong>Število prijav:</strong> {selectedCamp.current_count || 0} potrjenih
                      {selectedCamp.waitlist_count > 0 && ` + ${selectedCamp.waitlist_count} na čakalni listi`}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              /* Preview Tab */
              <div className="space-y-4">
                {/* Email Header Preview */}
                <div className="bg-stone-800/50 rounded-lg p-4 border border-stone-700">
                  <div className="flex items-center gap-2 text-stone-400 text-sm mb-2">
                    <span className="font-medium">Od:</span>
                    <span>LD Rakek &lt;ld.rakek@lovskazveza.si&gt;</span>
                  </div>
                  <div className="flex items-center gap-2 text-stone-400 text-sm mb-2">
                    <span className="font-medium">Za:</span>
                    <span>
                      {singleRecipient 
                        ? `${singleRecipient.parent_name} <${singleRecipient.parent_email}>`
                        : emailData.sendTo === 'all' ? 'Vsi prijavljeni starši'
                        : emailData.sendTo === 'confirmed' ? 'Starši potrjenih prijav'
                        : 'Starši na čakalni listi'
                      }
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-white">
                    <span className="font-medium text-stone-400">Zadeva:</span>
                    <span className="font-semibold">{emailData.subject || '(ni zadeve)'}</span>
                  </div>
                </div>

                {/* Email Body Preview */}
                <div className="border border-stone-700 rounded-lg overflow-hidden">
                  <div className="bg-stone-800 px-4 py-2 border-b border-stone-700">
                    <span className="text-stone-400 text-sm">Predogled emaila</span>
                  </div>
                  <div 
                    className="bg-white p-6 min-h-[400px]"
                    style={{ colorScheme: 'light' }}
                  >
                    {/* Simulate the email wrapper from backend */}
                    <div style={{ fontFamily: 'Arial, sans-serif', maxWidth: '600px', margin: 'auto', color: '#1f2937' }}>
                      <div style={{ background: '#065f46', color: '#fff', padding: '20px', borderRadius: '8px 8px 0 0' }}>
                        <h1 style={{ margin: 0, fontSize: '22px' }}>Lovska družina Rakek</h1>
                        <p style={{ margin: '4px 0 0', opacity: 0.85 }}>
                          {selectedCamp?.title_sl || 'Lovski tabor'}
                        </p>
                      </div>
                      <div style={{ 
                        background: '#f9fafb', 
                        padding: '22px', 
                        borderRadius: '0 0 8px 8px', 
                        border: '1px solid #e5e7eb', 
                        borderTop: 'none' 
                      }}>
                        {(selectedCamp?.start_date || selectedCamp?.end_date) && (
                          <p style={{ color: '#6b7280', marginBottom: '16px' }}>
                            <strong>Termin:</strong> {selectedCamp.start_date} 
                            {selectedCamp.end_date && ` – ${selectedCamp.end_date}`}
                            {selectedCamp.location && ` · ${selectedCamp.location}`}
                          </p>
                        )}
                        <div 
                          style={{ lineHeight: 1.7, color: '#374151' }}
                          dangerouslySetInnerHTML={{ __html: emailData.content || '<p style="color:#9ca3af">(Ni vsebine)</p>' }}
                        />
                        <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '24px', borderTop: '1px solid #e5e7eb', paddingTop: '14px' }}>
                          Če imate vprašanja, nam pišite na <a href="mailto:ld.rakek@lovskazveza.si" style={{ color: '#065f46' }}>ld.rakek@lovskazveza.si</a>.
                        </p>
                        <p style={{ color: '#9ca3af', fontSize: '11px', marginTop: '12px' }}>
                          Sporočilo ste prejeli, ker je vaš otrok prijavljen na lovski tabor Lovske družine Rakek.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {!emailData.content && (
                  <p className="text-amber-400 text-sm text-center">
                    ⚠️ Vnesite vsebino emaila v zavihku &quot;Uredi&quot;
                  </p>
                )}
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowEmailModal(false)}
                className="text-stone-400"
              >
                Prekliči
              </Button>
              <Button
                type="button"
                onClick={handleSendInfoEmail}
                disabled={emailSending || !emailData.subject || !emailData.content}
                className="bg-blue-600 hover:bg-blue-500"
              >
                {emailSending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Pošiljam...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Pošlji email
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ============ CAMP LIST VIEW ============
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Tent className="w-6 h-6 text-emerald-400" />
          <h2 className="text-xl font-semibold text-white">Lovski tabori</h2>
          <span className="px-2 py-1 text-xs bg-stone-800 text-stone-400 rounded-full">
            {camps.length}
          </span>
        </div>
        {!showForm && (
          <Button
            data-testid="add-camp-btn"
            onClick={() => { setFormData({ ...EMPTY_CAMP, year: new Date().getFullYear() }); setShowForm(true); }}
            className="bg-emerald-600 hover:bg-emerald-500"
          >
            <Plus className="w-4 h-4 mr-2" /> Dodaj tabor
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="bg-stone-800/50 border-stone-700/50 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingId ? 'Uredi tabor' : 'Nov tabor'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="camp-form">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-stone-300">Leto</Label>
                <Input
                  data-testid="camp-year"
                  type="number" required
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  className="bg-stone-900/50 border-stone-700 text-white"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-stone-300">Lokacija</Label>
                <Input
                  data-testid="camp-location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="bg-stone-900/50 border-stone-700 text-white"
                  placeholder="npr. Pranger"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-stone-300">Maks. udeležencev</Label>
                <Input
                  data-testid="camp-max"
                  type="number" min="1"
                  value={formData.max_participants}
                  onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                  className="bg-stone-900/50 border-stone-700 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-stone-300">Naslov (SL)</Label>
                <Input
                  data-testid="camp-title-sl"
                  required
                  value={formData.title_sl}
                  onChange={(e) => setFormData({ ...formData, title_sl: e.target.value })}
                  className="bg-stone-900/50 border-stone-700 text-white"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-stone-300">Naslov (EN)</Label>
                <Input
                  data-testid="camp-title-en"
                  required
                  value={formData.title_en}
                  onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                  className="bg-stone-900/50 border-stone-700 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-stone-300">Datum začetka</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="bg-stone-900/50 border-stone-700 text-white"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-stone-300">Datum konca</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="bg-stone-900/50 border-stone-700 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-stone-300">Opis (SL)</Label>
                <Textarea
                  value={formData.description_sl}
                  onChange={(e) => setFormData({ ...formData, description_sl: e.target.value })}
                  className="bg-stone-900/50 border-stone-700 text-white min-h-[90px]"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-stone-300">Opis (EN)</Label>
                <Textarea
                  value={formData.description_en}
                  onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                  className="bg-stone-900/50 border-stone-700 text-white min-h-[90px]"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-stone-300">Naslovna slika</Label>
              <ImageUploader
                images={formData.image}
                onChange={(img) => setFormData({ ...formData, image: img })}
                single={true}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-stone-300">Galerija slik (neobvezno)</Label>
              <ImageUploader
                images={formData.images}
                onChange={(imgs) => setFormData({ ...formData, images: imgs })}
                single={false}
              />
            </div>

            <div className="flex flex-wrap items-center gap-6 pt-2">
              <label className="flex items-center gap-2 text-stone-300">
                <Switch
                  checked={formData.registration_open}
                  onCheckedChange={(v) => setFormData({ ...formData, registration_open: v })}
                />
                Prijave odprte
              </label>
              <label className="flex items-center gap-2 text-stone-300">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
                Aktiven (viden na spletu)
              </label>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-500" data-testid="save-camp-btn">
                <Save className="w-4 h-4 mr-2" /> {editingId ? 'Shrani' : 'Dodaj'}
              </Button>
              <Button
                type="button" variant="outline"
                onClick={resetForm}
                className="border-stone-700 text-stone-300 hover:bg-stone-800"
              >
                <X className="w-4 h-4 mr-2" /> Prekliči
              </Button>
            </div>
          </form>
        </Card>
      )}

      <div className="grid gap-4">
        {camps.length === 0 ? (
          <Card className="bg-stone-800/50 border-stone-700/50 p-8 text-center">
            <Tent className="w-12 h-12 text-stone-600 mx-auto mb-3" />
            <p className="text-stone-400">Še ni dodanih taborov.</p>
          </Card>
        ) : (
          camps.map((camp) => (
            <Card
              key={camp.id}
              className="bg-stone-800/50 border-stone-700/50 p-4 hover:border-emerald-600/30 transition-colors"
              data-testid={`camp-row-${camp.id}`}
            >
              <div className="flex items-center gap-4 flex-wrap">
                <div className="text-3xl font-bold text-emerald-400 w-16">{camp.year}</div>
                <div className="flex-1 min-w-[200px]">
                  <div className="text-white font-semibold">{camp.title_sl}</div>
                  <div className="text-stone-400 text-sm">
                    {camp.start_date || '—'}{camp.end_date ? ` – ${camp.end_date}` : ''} · {camp.location || '—'}
                  </div>
                  <div className="text-stone-400 text-sm mt-0.5">
                    <span className={camp.is_full ? 'text-amber-400 font-medium' : ''}>
                      {camp.current_count || 0} / {camp.max_participants} prijav
                    </span>
                    {camp.waitlist_count > 0 && (
                      <span className="text-orange-400 ml-2">
                        + {camp.waitlist_count} na čakalni listi
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 mt-1.5 flex-wrap">
                    {camp.is_active ? (
                      <Badge className="bg-emerald-700/30 text-emerald-300 border border-emerald-600/40">Aktiven</Badge>
                    ) : (
                      <Badge className="bg-stone-700/30 text-stone-400 border border-stone-600/40">Skrit</Badge>
                    )}
                    {camp.registration_open ? (
                      <Badge className="bg-blue-700/30 text-blue-300 border border-blue-600/40">
                        {camp.is_full ? 'Sprejema na čakalno listo' : 'Prijave odprte'}
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-700/30 text-amber-300 border border-amber-600/40">Prijave zaprte</Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={() => openRegistrations(camp)}
                    className="bg-emerald-600 hover:bg-emerald-500"
                    data-testid={`view-regs-btn-${camp.id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" /> Prijave
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleRegistrations(camp)}
                    className={camp.registration_open 
                      ? "border-amber-700/50 text-amber-400 hover:bg-amber-900/20" 
                      : "border-emerald-700/50 text-emerald-400 hover:bg-emerald-900/20"
                    }
                    title={camp.registration_open ? 'Zapri prijave' : 'Odpri prijave'}
                    data-testid={`toggle-reg-btn-${camp.id}`}
                  >
                    {camp.registration_open ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleActive(camp)}
                    className={camp.is_active 
                      ? "border-stone-600 text-stone-400 hover:bg-stone-800" 
                      : "border-emerald-700/50 text-emerald-400 hover:bg-emerald-900/20"
                    }
                    title={camp.is_active ? 'Skrij tabor' : 'Prikaži tabor'}
                    data-testid={`toggle-active-btn-${camp.id}`}
                  >
                    {camp.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => handleEdit(camp)}
                    className="border-stone-700 text-stone-300 hover:bg-stone-800"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => handleDelete(camp.id)}
                    className="border-red-900/50 text-red-400 hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

const DetailRow = ({ label, children }) => (
  <div className="flex gap-3">
    <span className="text-stone-400 w-44 flex-shrink-0">{label}:</span>
    <span className="text-stone-100 break-words">{children}</span>
  </div>
);

const ConsentRow = ({ ok, label }) => (
  <div className="flex items-center gap-2">
    <span className={`inline-block w-2.5 h-2.5 rounded-full ${ok ? 'bg-emerald-400' : 'bg-red-500'}`} />
    <span className={ok ? 'text-emerald-300' : 'text-red-300'}>{label}</span>
  </div>
);

export default AdminCamps;
